<template>
  <div class="pass-upper-standard">
    <div class="ticket-area">
      <div
        class="ticket-title"
        :style="{ backgroundColor: upperColors.backgroundColor }"
      >
        <h1
          class="icon-title icon-title--ticket"
          :style="{
            color: upperColors.fontColor,
            '--icon-filter':
              upperColors.fontColor === '#000000'
                ? 'invert(1) brightness(0.2)'
                : 'brightness(0) invert(1)',
          }"
        >
          {{ passName }}
        </h1>
      </div>
      <div class="ticket-data-wrap">
        <dl class="ticket-data">
          <dt>現在時刻</dt>
          <dd class="text--red">
            <span class="text--small">{{ currentYear }}.</span
            >{{ currentDateTimeWithoutYear }}
          </dd>

          <dt>利用期間</dt>
          <dd>
            <div class="period-wrap">
              <div class="period-col">
                <span class="period-sublabel">利用開始日</span>
                <span class="period-date">
                  <span class="text--small">{{ startYear }}.</span
                  >{{ startMonthDay }}
                </span>
              </div>
              <span class="period-separator">〜</span>
              <div class="period-col">
                <span class="period-sublabel">利用終了日</span>
                <span class="period-date">
                  <span class="text--small">{{ endYear }}.</span
                  >{{ endMonthDay }}
                </span>
              </div>
            </div>
          </dd>

          <div v-if="divisions.length > 0" class="ticket-data__inline">
            <template v-for="division in divisions" :key="division.label">
              <dt>{{ division.label }}</dt>
              <dd>
                <span
                  class="text--big"
                  :class="{
                    'text--red': division.division === PASSENGER_TYPE_CHILD,
                  }"
                  >{{ division.count }}</span
                >
              </dd>
            </template>
          </div>

          <template v-if="sectionVisible">
            <dt>利用区間</dt>
            <dd>
              {{ sectionName
              }}<span v-if="sectionSuffix" class="text--small">{{
                sectionSuffix
              }}</span>
            </dd>
          </template>

          <template v-if="masterPassName">
            <dt>券種名</dt>
            <dd>{{ masterPassName }}</dd>
          </template>

          <template v-if="validArea">
            <dt>利用エリア</dt>
            <dd>{{ validArea }}</dd>
          </template>

          <dt>管理番号</dt>
          <dd>{{ book.book_group_id }}</dd>
        </dl>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from "vue";

import bookGroupService from "@/services/bookGroup";
import myTicketsCommonService from "@/services/common/myTickets";
import moment from "@/services/moment";
import passService from "@/services/pass";
import { useCurrentTime } from "@/composables/useCurrentTime";

const PASSENGER_TYPE_CHILD = "child";

export default {
  name: "ThePassUpperStandard",
  props: {
    upperColors: {
      type: Object,
      required: false,
      default: () => ({
        backgroundColor: "#cccccc",
        fontColor: "#000000",
      }),
    },
    book: {
      type: Object,
      required: true,
    },
    pass: {
      type: Object,
      required: true,
    },
    masterBook: {
      type: Object,
      required: true,
    },
    masterPass: {
      type: Object,
      required: false,
      default: null,
    },
    bookGroup: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const { currentYear, currentDateTimeWithoutYear } = useCurrentTime();

    const divisions = computed(() =>
      bookGroupService.divisionArrange(props.bookGroup.books)
    );

    const startAt = computed(() =>
      passService.getStartAt(props.pass, props.book)
    );

    const limitedAt = computed(() =>
      passService.getLimitedAt(props.pass, props.book)
    );

    const limitedAtMinusStartHour = computed(() =>
      myTicketsCommonService.getDiffLimitedAtMinusStartHour(
        limitedAt.value,
        props.masterBook.start_hour
      )
    );

    const startMoment = computed(() => moment(new Date(startAt.value)));
    const startYear = computed(() => startMoment.value.format("YYYY"));
    const startMonthDay = computed(() => startMoment.value.format("MM.DD"));

    const endMoment = computed(() => moment(limitedAtMinusStartHour.value));
    const endYear = computed(() => endMoment.value.format("YYYY"));
    const endMonthDay = computed(() => endMoment.value.format("MM.DD"));

    const sectionVisible = computed(
      () => !!props.book.price && props.pass.display_section_type !== "none"
    );

    const isArrivalDisplay = computed(
      () => props.pass.display_section_type === "arrival"
    );
    const isReturnTrip = computed(() => props.pass.is_return_trip);

    const sectionName = computed(() =>
      passService.getSectionName(
        props.book,
        isReturnTrip.value,
        isArrivalDisplay.value
      )
    );

    const sectionSuffix = computed(() =>
      passService.getSectionSuffix(
        props.book,
        isReturnTrip.value,
        isArrivalDisplay.value
      )
    );

    const masterPassName = computed(() => props.masterPass?.name ?? null);

    const validArea = computed(
      () => props.masterBook?.content?.description_json?.ValidArea ?? null
    );

    const passName = computed(() => props.pass.name);

    return {
      book: computed(() => props.book),
      passName,
      divisions,
      PASSENGER_TYPE_CHILD,
      currentYear,
      currentDateTimeWithoutYear,
      startYear,
      startMonthDay,
      endYear,
      endMonthDay,
      sectionVisible,
      sectionName,
      sectionSuffix,
      masterPassName,
      validArea,
      upperColors: props.upperColors,
    };
  },
};
</script>

<style lang="scss" scoped>
$ticket-header-bg: #f3f3f3;
$ticket-border-color: #e0e0e0;
$ticket-alert-color: #eb5757;

.pass-upper-standard {
  padding: 23px 15px 8px 15px;
}

.ticket-area {
  font-size: 14px;
  display: flex;
  flex-direction: column;
  margin: 0;
  overflow: hidden;
}

.ticket-title {
  padding: 6px 15px;
  background: $ticket-header-bg;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  position: relative;
  border: 1px solid $ticket-border-color;
  border-bottom: none;
  border-radius: 16px 16px 0 0;

  &::before,
  &::after {
    content: "";
    position: absolute;
    width: 24px;
    height: 24px;
    background: #fff;
    border-radius: 50%;
    border: 1px solid $ticket-border-color;
    bottom: -12px;
    left: -12px;
    z-index: 10;
  }

  &::after {
    left: auto;
    right: -12px;
  }
}

.icon-title {
  padding-left: 38px;
  font-weight: 700;
  font-size: 16px;
  line-height: 1.5;
  text-align: left;
  position: relative;
  margin: 0;
  color: #333333;

  &::before {
    content: "";
    height: 28px;
    width: 28px;
    display: block;
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    background-repeat: no-repeat;
    background-position: center;
    background-size: contain;
  }
}

.icon-title--ticket::before {
  background-image: url("~@/assets/img/icon/svg/icon_ticket.svg");
  filter: var(--icon-filter, invert(1) brightness(0.2));
}

.ticket-data-wrap {
  border: 1px solid $ticket-border-color;
  border-top: none;
  border-radius: 0 0 16px 16px;
  overflow: hidden;
}

.ticket-data {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 0;
  padding: 10px 25px;

  dt {
    width: 80px;
    color: #828282;
    padding: 4px 0;
  }

  dd {
    width: calc(100% - 80px);
    font-weight: bold;
    padding: 4px 0;
    color: #333333;

    &.text--red {
      color: $ticket-alert-color;
    }
  }
}

.ticket-data__inline {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  width: 100%;

  dd {
    width: calc(50% - 85px);

    &:nth-of-type(1) {
      margin-right: 10px;
    }
  }
}

.period-wrap {
  display: flex;
  align-items: flex-end;
  gap: 2px;
}

.period-col {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.period-sublabel {
  font-size: 10px;
  font-weight: normal;
  color: #333333;
}

.period-separator {
  font-weight: bold;
  color: #333333;
}

.text--small {
  font-size: 10px;
  font-weight: normal;
}

.text--big {
  font-size: 28px;
}

.text--red {
  color: $ticket-alert-color;
}
</style>
